/**
 * Pantalla de revisión manual de rutas de NS no encontradas en DispatchTrack.
 *
 * Contexto de negocio (ver docs_myspread/revision_ns/Analisis_Caso_Borde_Ruta_No_Encontrada.md
 * y docs_myspread/revision_ns/Adicion_Revision_Manual_y_Resumen_Diario.md §1):
 * cuando el proceso diario de reconciliación de NS no encuentra una ruta completa en
 * DispatchTrack, no la marca automáticamente (para no arriesgar falsos positivos) — el caso
 * queda registrado aquí para que Operaciones lo revise manualmente y deje constancia.
 *
 * Esta pantalla es solo de visibilidad + registro de revisión: NO incluye "buscar
 * coincidencias" ni "reconciliar automáticamente" (eso vive en Control Semanal, sobre
 * ítems huérfanos). La acción correctiva, si corresponde, la hace Operaciones directamente
 * sobre la guía/ruta desde las pantallas ya existentes.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Form, Alert, Card, Row, Col } from 'react-bootstrap';
import * as FaIcons from 'react-icons/fa';
import Swal from 'sweetalert2';

import SimpleControlGestionLayout from '../../components/SimpleControlGestionLayout';
import MarcarRevisadoModal from './components/MarcarRevisadoModal';
import EjecutarReconciliacionManualModal from './components/EjecutarReconciliacionManualModal';
import { useNsReconciliacion } from '../../../Mantenedores/hooks/useNsReconciliacion';
import { formatFecha, formatFechaHora } from './utils/helpers';

const todayStr = () => new Date().toISOString().split('T')[0];

const RutasPendientesRevision = () => {
  const { handleGetRutasPendientesRevision, handleMarcarRutaPendienteRevisada, handleEjecutarReconciliacionManual } = useNsReconciliacion();

  const [rutas, setRutas] = useState([]);
  const [showRevisadas, setShowRevisadas] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState(null);

  // Modal de "marcar revisado"
  const [showModal, setShowModal] = useState(false);
  const [selectedRuta, setSelectedRuta] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Reconciliación manual por fecha (ver Adicion_Trigger_Manual_Por_Fecha.md, §3, y su
  // rediseño asíncrono en Adicion_Rediseno_Async_Trigger_Manual.md, §5): la solicitud ahora
  // se encola y responde casi de inmediato (202) - el resultado real (métricas, nuevas rutas
  // pendientes de revisión) se calcula después, en background.
  const [manualFecha, setManualFecha] = useState('');
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualResult, setManualResult] = useState(null); // { success, error, fecha, mensaje } | null

  const sortByFechaDesc = (list) =>
    [...list].sort((a, b) => (a.fecha < b.fecha ? 1 : a.fecha > b.fecha ? -1 : 0));

  const loadData = useCallback(async () => {
    setPageLoading(true);
    setPageError(null);

    try {
      // El backend soporta revisado=false (default, solo pendientes) o
      // revisado='all' (pendientes + revisadas en un solo llamado) - ver
      // routes/mantenedores/ns_reconciliacion_revision.py, confirmado contra el
      // código real durante la Adición 1 (ya no requiere combinar 2 llamadas).
      const response = await handleGetRutasPendientesRevision({
        revisado: showRevisadas ? 'all' : false
      });

      if (response.success) {
        setRutas(sortByFechaDesc(response.data || []));
      } else {
        setRutas([]);
        setPageError(response.error);
      }
    } catch (error) {
      console.error('Error al cargar rutas pendientes de revisión:', error);
      setPageError('Error al cargar rutas pendientes de revisión');
      setRutas([]);
    } finally {
      setPageLoading(false);
    }
  }, [showRevisadas]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ========== HANDLERS ==========
  const handleToggleShowRevisadas = (e) => {
    setShowRevisadas(e.target.checked);
  };

  const handleOpenModal = (ruta) => {
    setSelectedRuta(ruta);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (modalLoading) return;
    setShowModal(false);
    setSelectedRuta(null);
  };

  const handleExecuteMarcarRevisado = async (observaciones) => {
    if (!selectedRuta) return;

    setModalLoading(true);
    try {
      const response = await handleMarcarRutaPendienteRevisada(selectedRuta.id, observaciones);

      if (response.success) {
        setShowModal(false);
        setSelectedRuta(null);

        await Swal.fire({
          icon: 'success',
          title: 'Ruta marcada como revisada',
          text: response.message || 'Se registró la revisión correctamente',
          timer: 2500,
          showConfirmButton: false
        });

        await loadData();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error al marcar como revisada',
          text: response.error || 'Ocurrió un error al registrar la revisión',
          confirmButtonText: 'Aceptar'
        });
      }
    } catch (error) {
      console.error('Error al marcar ruta como revisada:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al registrar la revisión',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setModalLoading(false);
    }
  };

  // ========== HANDLERS: reconciliación manual por fecha ==========
  const handleManualFechaChange = (e) => {
    setManualFecha(e.target.value);
  };

  const handleOpenManualModal = () => {
    if (!manualFecha || manualLoading) return;
    setManualResult(null);
    setShowManualModal(true);
  };

  const handleCloseManualModal = () => {
    if (manualLoading) return;
    setShowManualModal(false);
  };

  const handleExecuteReconciliacionManual = async () => {
    if (!manualFecha) return;

    setManualLoading(true);
    setManualResult(null);

    try {
      const response = await handleEjecutarReconciliacionManual(manualFecha);
      setShowManualModal(false);

      if (response.success) {
        // El backend ahora encola la solicitud y responde de inmediato (202) - ya no
        // devuelve métricas finales (esas se calculan después, en background). No es
        // imprescindible refrescar la tabla ahora mismo: el resultado real puede tardar
        // unos minutos en reflejarse; el usuario puede usar el botón "Actualizar" más tarde.
        setManualResult({
          success: true,
          fecha: response.fecha || manualFecha,
          mensaje: response.mensaje
        });
      } else {
        setManualResult({ success: false, error: response.error || 'Ocurrió un error al ejecutar la reconciliación manual' });
      }
    } catch (error) {
      console.error('Error al ejecutar la reconciliación manual de NS:', error);
      setShowManualModal(false);
      setManualResult({ success: false, error: 'Ocurrió un error al ejecutar la reconciliación manual' });
    } finally {
      setManualLoading(false);
    }
  };

  // ========== RENDER ==========
  return (
    <SimpleControlGestionLayout
      title="Rutas Pendientes de Revisión"
      subtitle="Rutas de NS que el proceso diario no pudo encontrar en DispatchTrack — requieren revisión manual de Operaciones"
      icon={<FaIcons.FaExclamationTriangle />}
      iconColor="#dc3545"
      breadcrumbs={[{ label: 'Rutas Pendientes de Revisión' }]}
    >
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <FaIcons.FaPlayCircle className="me-2" />
            Ejecutar reconciliación manual por fecha
          </h5>
        </Card.Header>
        <Card.Body>
          <p className="text-muted">
            Fuerza la reconciliación de NS para una fecha específica sin esperar a la corrida
            automática de la madrugada. Es una acción real de escritura en producción — se
            pedirá confirmación antes de ejecutarla.
          </p>
          <Row className="align-items-end g-2">
            <Col xs={12} md={4} lg={3}>
              <Form.Group>
                <Form.Label className="fw-bold">Fecha a reconciliar</Form.Label>
                <Form.Control
                  type="date"
                  value={manualFecha}
                  max={todayStr()}
                  onChange={handleManualFechaChange}
                  disabled={manualLoading}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={5}>
              <Button
                variant="danger"
                onClick={handleOpenManualModal}
                disabled={!manualFecha || manualLoading}
                className="text-nowrap d-inline-flex align-items-center justify-content-center w-100"
                style={{ minWidth: 260 }}
              >
                {manualLoading ? (
                  <>
                    <FaIcons.FaSpinner className="fa-spin me-2" />
                    Encolando solicitud...
                  </>
                ) : (
                  <>
                    <FaIcons.FaPlayCircle className="me-2" />
                    Ejecutar reconciliación manual
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {manualResult && (
            <Alert
              variant={manualResult.success ? 'success' : 'danger'}
              className="mt-3 mb-0"
              dismissible
              onClose={() => setManualResult(null)}
            >
              {manualResult.success ? (
                <>
                  <FaIcons.FaCheckCircle className="me-2" />
                  <strong>Solicitud de reconciliación de {formatFecha(manualResult.fecha)} encolada correctamente.</strong>
                  <p className="mb-0 mt-2">
                    {manualResult.mensaje ||
                      'El resultado se procesará en background y puede tardar unos minutos en reflejarse.'}{' '}
                    Revise la tabla de abajo (si se detectan rutas no encontradas en
                    DispatchTrack) o el resumen diario por correo. Puede usar el botón
                    &quot;Actualizar&quot; para refrescar la tabla cuando lo desee.
                  </p>
                </>
              ) : (
                <>
                  <FaIcons.FaExclamationCircle className="me-2" />
                  <strong>No se pudo ejecutar la reconciliación manual:</strong> {manualResult.error}
                </>
              )}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <Form.Check
          type="switch"
          id="toggle-mostrar-revisadas"
          label="Mostrar también las ya revisadas"
          checked={showRevisadas}
          onChange={handleToggleShowRevisadas}
        />

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={loadData}
          disabled={pageLoading}
        >
          {pageLoading ? (
            <FaIcons.FaSpinner className="fa-spin me-2" />
          ) : (
            <FaIcons.FaSyncAlt className="me-2" />
          )}
          Actualizar
        </Button>
      </div>

      {pageError && (
        <Alert variant="danger">
          <FaIcons.FaExclamationCircle className="me-2" />
          {pageError}
        </Alert>
      )}

      {pageLoading ? (
        <div className="text-center py-5">
          <FaIcons.FaSpinner className="fa-spin" size={40} />
          <p className="mt-3">Cargando rutas pendientes de revisión...</p>
        </div>
      ) : rutas.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <FaIcons.FaCheckCircle size={40} className="mb-3 text-success" />
          <p>
            {showRevisadas
              ? 'No hay rutas registradas para revisión.'
              : 'No hay rutas pendientes de revisión en este momento.'}
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="mb-0">
            <thead className="table-primary">
              <tr>
                <th className="text-white" style={{ backgroundColor: '#007bff' }}>Fecha</th>
                <th className="text-white" style={{ backgroundColor: '#007bff' }}>Código de Ruta</th>
                <th className="text-white text-center" style={{ backgroundColor: '#007bff' }}>Guías no-definitivas</th>
                <th className="text-white text-center" style={{ backgroundColor: '#007bff' }}>Estado</th>
                <th className="text-white" style={{ backgroundColor: '#007bff' }}>Revisado por</th>
                <th className="text-white" style={{ backgroundColor: '#007bff' }}>Revisado el</th>
                <th className="text-white" style={{ backgroundColor: '#007bff' }}>Observaciones</th>
                <th className="text-white text-center" style={{ backgroundColor: '#007bff' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {rutas.map((ruta) => (
                <tr key={ruta.id ?? `${ruta.ruta_id}-${ruta.fecha}`}>
                  <td>{formatFecha(ruta.fecha)}</td>
                  <td className="fw-bold">{ruta.ruta}</td>
                  <td className="text-center">{ruta.guias_no_definitivas}</td>
                  <td className="text-center">
                    {ruta.revisado ? (
                      <Badge bg="success">Revisado</Badge>
                    ) : (
                      <Badge bg="warning" text="dark">Pendiente</Badge>
                    )}
                  </td>
                  <td>{ruta.revisado_por || '-'}</td>
                  <td>{ruta.revisado ? formatFechaHora(ruta.revisado_fecha) : '-'}</td>
                  <td>{ruta.observaciones || '-'}</td>
                  <td className="text-center">
                    {!ruta.revisado && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleOpenModal(ruta)}
                      >
                        <FaIcons.FaCheck className="me-1" />
                        Marcar revisado
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <MarcarRevisadoModal
        show={showModal}
        onHide={handleCloseModal}
        ruta={selectedRuta}
        onExecute={handleExecuteMarcarRevisado}
        loading={modalLoading}
      />

      <EjecutarReconciliacionManualModal
        show={showManualModal}
        onHide={handleCloseManualModal}
        fecha={manualFecha}
        onConfirm={handleExecuteReconciliacionManual}
        loading={manualLoading}
      />
    </SimpleControlGestionLayout>
  );
};

export default RutasPendientesRevision;
